import React, { useEffect, useState, useCallback } from 'react';
import { supabase, uploadImage, deleteImageByUrl } from '../../services/supabaseClient';
import Modal from '../Modal';
import ImageUploader from '../ImageUploader';

const PublicidadSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    empresa: '',
    number: 1,
    image_url: '',
    enlace: '',
  });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('publicity')
      .select('*')
      .order('number', { ascending: true });
    if (error) console.error(error);
    else setItems(data || []);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreateModal = () => {
    setEditing(null);
    setFormData({
      empresa: '',
      number: 1,
      image_url: '',
      enlace: '',
    });
    setImageFile(null);
    setFormError('');
    setShowModal(true);
  };

  const openEditModal = (item) => {
    setEditing(item);
    setFormData({
      empresa: item.empresa || '',
      number: item.number || 1,
      image_url: item.image_url || '',
      enlace: item.enlace || '',
    });
    setImageFile(null);
    setFormError('');
    setShowModal(true);
  };

  const handleDelete = async (item) => {
    if (!window.confirm('¿Eliminar esta publicidad?')) return;
    const { error } = await supabase.from('publicity').delete().eq('id', item.id);
    if (error) {
      alert('Error: ' + error.message);
      return;
    }
    if (item.image_url) await deleteImageByUrl(item.image_url);
    fetchData();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setSaving(true);
    try {
      let imageUrl = formData.image_url;
      if (imageFile) {
        if (editing && editing.image_url) await deleteImageByUrl(editing.image_url);
        imageUrl = await uploadImage(imageFile);
      } else if (imageFile === null && formData.image_url === '') {
        if (editing && editing.image_url) await deleteImageByUrl(editing.image_url);
        imageUrl = null;
      }

      if (!imageUrl) {
        throw new Error('La imagen es obligatoria para la publicidad');
      }

      const payload = {
        empresa: formData.empresa,
        number: Number(formData.number),
        image_url: imageUrl,
        // Sin enlace el banner se muestra como imagen, sin ser pulsable.
        enlace: formData.enlace.trim() || null,
      };

      if (editing) {
        const { error } = await supabase
          .from('publicity')
          .update(payload)
          .eq('id', editing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('publicity').insert([payload]);
        if (error) throw error;
      }

      setShowModal(false);
      fetchData();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="add-button-container">
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Agregar Publicidad
        </button>
      </div>

      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Acciones</th>
                <th>Imagen</th>
                <th>Empresa</th>
                <th>Enlace</th>
                <th>Número</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="6" className="empty-row">
                    Sin publicidad registrada
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <div className="actions-cell">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => openEditModal(item)}
                        >
                          Editar
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(item)}
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                    <td>
                      {item.image_url ? (
                        <img src={item.image_url} alt="" className="table-image" loading="lazy" decoding="async" />
                      ) : (
                        <div className="no-image">Sin foto</div>
                      )}
                    </td>
                    <td>{item.empresa}</td>
                    <td>
                      {item.enlace ? (
                        <a href={item.enlace} target="_blank" rel="noreferrer" title={item.enlace}>
                          Ver destino
                        </a>
                      ) : (
                        <span style={{ color: '#999' }}>Sin enlace</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${item.number}`}>
                        Publicidad {item.number}
                      </span>
                    </td>
                    <td>
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString('es-PE')
                        : '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <Modal
          title={editing ? 'Editar Publicidad' : 'Agregar Publicidad'}
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button
                className="btn btn-secondary"
                onClick={() => setShowModal(false)}
                disabled={saving}
              >
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </>
          }
        >
          <form onSubmit={handleSubmit}>
            {formError && <div className="error-message">{formError}</div>}

            <div className="form-group">
              <label>Empresa *</label>
              <input
                type="text"
                value={formData.empresa}
                onChange={(e) => setFormData({ ...formData, empresa: e.target.value })}
                required
              />
            </div>

            <div className="form-group">
              <label>Posición / Número *</label>
              <select
                value={formData.number}
                onChange={(e) => setFormData({ ...formData, number: Number(e.target.value) })}
                required
              >
                <option value={1}>Publicidad 1</option>
                <option value={2}>Publicidad 2</option>
              </select>
              <small style={{ color: '#666', fontSize: '0.85em' }}>
                Define en qué posición se mostrará la publicidad en el sitio.
              </small>
            </div>

            <div className="form-group">
              <label>Enlace del anunciante</label>
              <input
                type="url"
                value={formData.enlace}
                onChange={(e) => setFormData({ ...formData, enlace: e.target.value })}
                placeholder="https://www.empresa.com"
              />
              <small style={{ color: '#666', fontSize: '0.85em' }}>
                A dónde lleva el banner al pulsarlo. Si se deja vacío, el anuncio
                se muestra como imagen sin ser pulsable.
              </small>
            </div>

            <ImageUploader
              currentImageUrl={formData.image_url}
              onFileSelect={(file) => {
                setImageFile(file);
                if (file === null) setFormData({ ...formData, image_url: '' });
              }}
              label="Imagen *"
            />

            {formData.image_url && !imageFile && (
              <div className="image-preview" style={{ marginTop: 10 }}>
                <img
                  src={formData.image_url}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 4 }}
                />
              </div>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
};

export default PublicidadSection;